import {useQuery} from '@tanstack/react-query';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {
  ApiError,
  Employee,
  EmployeeMap,
  EmployeeService,
  GratitudeService,
  HomeAgendaItem,
  HomeAgendaResponse,
  HomeJiraTicket,
  HomeJiraTicketsResponse,
  HomeMeetingDoc,
  HomeService,
  HomeStockQuote,
  MyPeople,
  WorkstreamAccess,
  WorkstreamBasicWithOutcomes,
} from 'client';
import {format, parseISO} from 'date-fns';
import {isCompanyEmail} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useEffect, useMemo, useRef} from 'react';
import {getService} from 'utilities';
import {AgendaItem} from 'views/home/dashboard/dashboardModel';
import {
  AGENDA_STALE_TIME_MS,
  EMPLOYEE_MAP_STALE_TIME_MS,
  GRATITUDES_STALE_TIME_MS,
  HOME_NEWS_QUERY_KEY,
  HOME_PEOPLE_QUERY_KEY,
  HOME_STOCK_QUERY_KEY,
  JIRA_STALE_TIME_MS,
  NEWS_STALE_TIME_MS,
  PEOPLE_MAP_QUERY_KEY,
  PEOPLE_STALE_TIME_MS,
  STOCK_STALE_TIME_MS,
} from 'views/home/homeQueryKeys';
import {useWorkstream} from 'views/workstreams/hooks';

import {zoneBucketKey, zoneFriendlyName} from './coreHours';
import {overlayStockQuote} from './dashboardDataMappers';
import {
  DashboardState,
  FeaturedPerson,
  JiraTicket,
  MeetingDoc,
  NewArrivalItem,
  NewsItem,
  TimezoneGroup,
} from './dashboardModel';
import {useHomeTopPriorities} from './useHomeTopPriorities';

// When the agenda comes back with a transiently-unavailable personal calendar,
// poll this many times at this interval before giving up (so a flaky cold fetch
// self-heals without a manual refresh, but a persistent outage stops cleanly).
const MAX_AGENDA_BG_RETRIES = 3;
const AGENDA_BG_RETRY_INTERVAL_MS = 4000;

const JIRA_STATUSES: ReadonlySet<JiraTicket['status']> = new Set([
  'not_started',
  'on_track',
  'at_risk',
  'complete',
]);

const LEGACY_JIRA_STATUS_MAP: Record<string, JiraTicket['status']> = {
  todo: 'not_started',
  in_progress: 'on_track',
  in_review: 'at_risk',
  done: 'complete',
};

const toJiraStatus = (status: string): JiraTicket['status'] => {
  if (JIRA_STATUSES.has(status as JiraTicket['status'])) {
    return status as JiraTicket['status'];
  }
  return LEGACY_JIRA_STATUS_MAP[status] ?? 'not_started';
};

const EMPTY_JIRA_RESPONSE: HomeJiraTicketsResponse = {
  tickets: [],
  truncated: false,
  limit: 25,
  issues_url: '',
};

/** Accepts the wrapped payload or the legacy bare ticket array from /api/v1/home/jira. */
const normalizeJiraResponse = (
  data: HomeJiraTicketsResponse | HomeJiraTicket[] | undefined
): HomeJiraTicketsResponse => {
  if (!data) {
    return EMPTY_JIRA_RESPONSE;
  }
  if (Array.isArray(data)) {
    return {...EMPTY_JIRA_RESPONSE, tickets: data};
  }
  return {
    tickets: data.tickets ?? [],
    truncated: data.truncated ?? false,
    limit: data.limit ?? EMPTY_JIRA_RESPONSE.limit,
    issues_url: data.issues_url ?? '',
  };
};

interface PersonCandidate {
  person: Employee;
  connection: string;
}

/**
 * Builds a "meet someone" candidate focused on the logged-in user's reporting
 * line going *up*: their +1 (skip-level) and above are the leaders they'd
 * benefit from getting to know, so those are prioritized. Falls back to the
 * direct manager, then teammates / direct reports if there's no upward chain.
 */
const buildFeaturedPerson = (
  directReports: Employee[],
  teamMembers: Employee[],
  reportingLine: Employee[],
  selfUserId: string | undefined
): FeaturedPerson | null => {
  const candidates: PersonCandidate[] = [];
  const seen = new Set<string>(selfUserId ? [selfUserId] : []);

  const add = (people: Employee[], connection: string) => {
    people.forEach((person) => {
      if (!person?.user_id || seen.has(person.user_id) || person.is_deactivated) {
        return;
      }
      seen.add(person.user_id);
      candidates.push({person, connection});
    });
  };

  // reportingLine[0] is the logged-in user, [1] their manager, [2] the
  // skip-level (+1), and so on up the chain.
  const up = reportingLine.slice(1);
  // Lead with the +1 (skip-level) and everyone above it.
  up.slice(1).forEach((person, index) => {
    add(
      [person],
      index === 0
        ? t('dashboard_meet_connection_skip_level').toString()
        : t('dashboard_meet_connection_levels_up', {count: index + 2}).toString()
    );
  });
  // Then the direct manager.
  add(up.slice(0, 1), t('dashboard_meet_connection_manager').toString());
  // Only if there's no upward reporting line, suggest someone lateral.
  if (!candidates.length) {
    add(teamMembers, t('dashboard_meet_connection_team').toString());
    add(directReports, t('dashboard_meet_connection_reports').toString());
  }

  if (!candidates.length) {
    return null;
  }

  const picked = candidates[new Date().getDate() % candidates.length];
  return {
    name: picked.person.name,
    ldap: picked.person.ldap,
    photoUrl: picked.person.profile_photo_url,
    role: picked.person.role ?? undefined,
    level: picked.person.level ?? undefined,
    team: picked.person.team ?? undefined,
    connection: picked.connection,
  };
};

/**
 * Groups the logged-in user's real circle (teammates, direct manager, direct
 * reports) by timezone, using the employee map (the one data source that
 * carries `time_zone` per person). People without a known timezone are skipped.
 *
 * People are bucketed by their *current* UTC offset rather than their raw IANA
 * identifier, so zones that present identically right now collapse into one row
 * (e.g. `America/Los_Angeles` + `America/Vancouver` → one "Pacific Time"). Zones
 * that don't observe DST drift in and out of these buckets seasonally:
 * `America/Regina` (Saskatchewan) folds in with Central in winter and shows as
 * its own row in summer when its offset genuinely differs. The bucket's label is
 * the friendly name shared by the most people in it. Ireland always gets its own
 * row rather than folding into the UK's (see `zoneBucketKey`).
 */
const buildTimezoneGroups = (
  circle: Employee[],
  employeeMap: EmployeeMap[],
  selfUserId: string | undefined
): TimezoneGroup[] => {
  const zoneById = new Map<string, EmployeeMap>();
  const zoneByLdap = new Map<string, EmployeeMap>();
  employeeMap.forEach((person) => {
    zoneById.set(person.user_id, person);
    zoneByLdap.set(person.ldap, person);
  });

  const now = Date.now();

  interface ZoneAccumulator {
    key: string;
    ldaps: string[];
    // Friendly name → member count, used to pick the bucket's dominant label.
    labelCounts: Map<string, number>;
    // Friendly name → a representative IANA zone, used to render the clock.
    labelZone: Map<string, string>;
  }
  const groups = new Map<string, ZoneAccumulator>();
  const seen = new Set<string>(selfUserId ? [selfUserId] : []);

  circle.forEach((person) => {
    if (!person?.user_id || seen.has(person.user_id) || person.is_deactivated) {
      return;
    }
    const mapEntry = zoneById.get(person.user_id) ?? zoneByLdap.get(person.ldap);
    const timeZone = mapEntry?.time_zone;
    if (!timeZone) {
      return;
    }
    seen.add(person.user_id);

    const key = zoneBucketKey(now, timeZone);
    const group: ZoneAccumulator = groups.get(key) ?? {
      key,
      ldaps: [],
      labelCounts: new Map(),
      labelZone: new Map(),
    };
    group.ldaps.push(person.ldap);
    const friendly = zoneFriendlyName(now, timeZone);
    group.labelCounts.set(friendly, (group.labelCounts.get(friendly) ?? 0) + 1);
    if (!group.labelZone.has(friendly)) {
      group.labelZone.set(friendly, timeZone);
    }
    groups.set(key, group);
  });

  return Array.from(groups.values()).map((group) => {
    // Most-common friendly name wins; ties break alphabetically for stability.
    const [label] = [...group.labelCounts.entries()].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
    )[0];
    return {
      id: `offset:${group.key}`,
      label,
      timeZone: group.labelZone.get(label)!,
      ldaps: group.ldaps,
      memberCount: group.ldaps.length,
    };
  });
};

const NEW_HIRE = 'new';

const newArrivalDetail = (person: MyPeople): string =>
  person.role ?? t('dashboard_new_arrival_default').toString();

// How "wow" a new arrival is: a rough seniority signal so "this high-level
// person just joined" surfaces first. Level ladders vary, so we combine a
// best-effort numeric read of the level string with org scope (report counts).
const newHireNotability = (person: MyPeople): number => {
  const levelDigits = person.level?.match(/(\d+)/);
  const levelRank = levelDigits ? Number(levelDigits[1]) : 0;
  const reportScope = Math.sqrt(person.total_report_count ?? 0);
  return levelRank + reportScope;
};

const orderNewArrivals = (people: MyPeople[]): MyPeople[] =>
  people
    .filter((p) => p.event === NEW_HIRE)
    .sort((a, b) => newHireNotability(b) - newHireNotability(a));

const isArchivedWorkstream = (dependent: WorkstreamBasicWithOutcomes): boolean =>
  Boolean(dependent.archived) || dependent.updates?.[0]?.status === 'archived';

/** Whether the viewer's tracker has any workstreams the tile should surface. */
const viewerHasWorkstreams = (tracker?: WorkstreamAccess): boolean =>
  (tracker?.dependencies ?? []).some((dependency) => {
    const dependent = dependency.dependent;
    return dependent.name !== 'Restricted' && !isArchivedWorkstream(dependent);
  });

export const mapHomeAgendaItem = (item: HomeAgendaItem): AgendaItem => ({
  id: item.id,
  title: item.title,
  start: item.start,
  end: item.end,
  allDay: item.all_day ?? false,
  source: item.source === HomeAgendaItem.source.COMPANY ? 'company' : 'personal',
  location: item.location ?? undefined,
  joinUrl: item.join_url ?? undefined,
  attendeeCount: item.attendee_count ?? undefined,
  description: item.description ?? undefined,
  calendarUrl: item.calendar_url ?? undefined,
  hasExternalGuests: (item.attendees ?? []).some(
    (attendee) =>
      !attendee.is_self && !!attendee.email && !isCompanyEmail(attendee.email.toLowerCase())
  ),
  docs: (item.docs ?? []).map(
    (doc): MeetingDoc => ({
      id: doc.id,
      title: doc.title,
      kind: doc.kind === HomeMeetingDoc.kind.PAPER ? 'paper' : 'file',
    })
  ),
});

const hasPersonalAgendaItems = (items: AgendaItem[]): boolean =>
  items.some((item) => item.source === 'personal');

/** Keep personal rows when a refetch only returns company events after a timeout. */
const mergeAgendaWithStalePersonal = (
  fresh: AgendaItem[],
  stalePersonal: AgendaItem[]
): AgendaItem[] => {
  const freshCompany = fresh.filter((item) => item.source === 'company');
  return [...stalePersonal, ...freshCompany].sort((a, b) => a.start - b.start);
};

/**
 * Overlays real API data onto the dashboard state scaffold. Tiles without a
 * backing endpoint stay empty once their query settles.
 */
export function useRealDashboardData(state: DashboardState): DashboardState {
  const {employee, teamMembers, directReports, reportingLine} = useAtomValue(loggedInEmployeeAtom);
  const lastPersonalAgendaRef = useRef<AgendaItem[]>([]);

  const {data: stock, isLoading: stockFetching} = useQuery<HomeStockQuote | null>({
    queryKey: HOME_STOCK_QUERY_KEY,
    queryFn: getService(HomeService).stockApiV1HomeStockGet,
    staleTime: STOCK_STALE_TIME_MS,
  });

  const {data: news, isLoading: newsFetching} = useQuery({
    queryKey: HOME_NEWS_QUERY_KEY,
    queryFn: getService(HomeService).newsApiV1HomeNewsGet,
    staleTime: NEWS_STALE_TIME_MS,
  });

  // New hires (`event === 'new'`) from the legacy Home people endpoint.
  // Birthdays, anniversaries, and PTO use peopleCalendarApiV1HomePeopleCalendarGet
  // in the Calendar tile instead.
  const {data: people, isLoading: peopleFetching} = useQuery({
    queryKey: HOME_PEOPLE_QUERY_KEY,
    queryFn: () =>
      getService(HomeService).peopleApiV1HomePeopleGet(
        encodeURIComponent(new Date().toISOString())
      ),
    staleTime: PEOPLE_STALE_TIME_MS,
  });

  const {data: gratitudes, isLoading: gratitudesFetching} = useQuery({
    queryKey: ['gratitudes', 'received'],
    queryFn: getService(GratitudeService).getGratitudesSentToUserApiV1GratitudesReceivedUserIdGet,
    staleTime: GRATITUDES_STALE_TIME_MS,
  });

  // The viewer's real open Jira tickets. Cached for a few minutes so repeated
  // renders / navigation don't re-hit Jira; the endpoint degrades to [] on error.
  const {data: jiraTickets, isFetched: jiraFetched} = useQuery({
    queryKey: ['dashboard', 'jira'],
    queryFn: getService(HomeService).myJiraTicketsApiV1HomeJiraGet,
    staleTime: JIRA_STALE_TIME_MS,
  });

  // The viewer's real calendar (linked Google/Outlook via Dropbox Calendar API).
  // The endpoint always returns 200 and degrades the *personal* portion to [] on
  // failure, so two distinct things can go wrong:
  //   1. The whole request fails (gateway timeout / 5xx). The global default is
  //      `retry: false`, so we retry with backoff here (skipping 4xx, which
  //      won't fix on retry) instead of leaving the tile empty until a refresh.
  //   2. The request succeeds but the personal calendar transiently failed with
  //      no server-side cache — surfaced as `personal_status === 'unavailable'`.
  //      React Query won't retry a 200, so we poll a few times in the background
  //      until it recovers, keeping the company events visible meanwhile.
  const agendaBgRetryCountRef = useRef(0);
  const {
    data: agendaResponse,
    isLoading: agendaFetching,
    isError: agendaFailed,
    dataUpdatedAt: agendaUpdatedAt,
  } = useQuery({
    queryKey: ['home', 'agenda'],
    queryFn: getService(HomeService).agendaApiV1HomeAgendaGet,
    staleTime: AGENDA_STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    refetchOnReconnect: true,
    refetchInterval: (query) =>
      query.state.data?.personal_status === HomeAgendaResponse.personal_status.UNAVAILABLE &&
      agendaBgRetryCountRef.current < MAX_AGENDA_BG_RETRIES
        ? AGENDA_BG_RETRY_INTERVAL_MS
        : false,
  });

  // Count consecutive "unavailable" responses (resetting on recovery) so the
  // background poll above is bounded. `agendaUpdatedAt` changes on every fetch
  // resolution, so this fires once per attempt even when the (empty) payload is
  // byte-identical between tries.
  useEffect(() => {
    if (!agendaResponse) return;
    if (agendaResponse.personal_status === HomeAgendaResponse.personal_status.UNAVAILABLE) {
      agendaBgRetryCountRef.current += 1;
    } else {
      agendaBgRetryCountRef.current = 0;
    }
  }, [agendaUpdatedAt, agendaResponse]);

  const agenda = agendaResponse?.items;

  // The employee map is the one source that carries `time_zone` per person,
  // so we use it to place the user's real team into timezone buckets.
  const {data: employeeMap, isFetched: employeeMapFetched} = useQuery({
    queryKey: PEOPLE_MAP_QUERY_KEY,
    queryFn: getService(EmployeeService).getEmployeeMapApiV1EmployeeMapGet,
    staleTime: EMPLOYEE_MAP_STALE_TIME_MS,
  });

  const hasStockData = Boolean(stock && Number.isFinite(stock.price));

  // Shares the React Query cache with MyWork in WorkstreamsTile (same ldap key).
  const {workstream: workstreamTracker, isLoading: workstreamFetching} = useWorkstream(
    employee?.ldap
  );
  const hasWorkstreams = viewerHasWorkstreams(workstreamTracker);

  // Shares the React Query cache with TopPrioritiesTile. Only fetched when the tile's flag is on.
  const {isTopPrioritiesTileEnabled} = useAtomValue(growthbookCacheAtom);
  const {data: topPriorities, isLoading: topPrioritiesFetching} = useHomeTopPriorities({
    enabled: isTopPrioritiesTileEnabled,
  });
  const hasTopPriorities = Boolean(topPriorities?.metrics?.length);

  // The "meet someone" and workstreams tiles depend on the logged-in profile
  // (teammates / reporting line / ldap), which loads into a jotai atom rather
  // than a query here, so we treat "profile not resolved yet" as loading.
  const profileLoading = !employee?.user_id;
  const loadingFlags = useMemo<Partial<DashboardState>>(
    () => ({
      stockLoading: stockFetching,
      newsLoading: newsFetching,
      newArrivalsLoading: peopleFetching,
      gratitudeLoading: gratitudesFetching,
      meetSomeoneLoading: profileLoading,
      workstreamsLoading: profileLoading || workstreamFetching,
      topPrioritiesLoading: topPrioritiesFetching,
      agendaLoading: agendaFetching,
      agendaCalendar: agendaResponse?.calendar ?? (agendaFailed ? 'error' : 'unknown'),
      jiraLoading: !jiraFetched,
      hasStockData,
      hasWorkstreams,
      hasTopPriorities,
    }),
    [
      stockFetching,
      newsFetching,
      peopleFetching,
      gratitudesFetching,
      profileLoading,
      workstreamFetching,
      agendaFetching,
      agendaResponse?.calendar,
      agendaFailed,
      jiraFetched,
      hasStockData,
      hasWorkstreams,
      topPrioritiesFetching,
      hasTopPriorities,
    ]
  );

  return useMemo(() => {
    let next = state;

    next = overlayStockQuote(next, stock);

    if (Array.isArray(news) && news.length) {
      next = {
        ...next,
        news: news.slice(0, 8).map(
          (post): NewsItem => ({
            id: String(post.id),
            postId: typeof post.id === 'number' ? post.id : undefined,
            title: post.title,
            date: post.created_at ? format(parseISO(post.created_at), 'MMM d') : '',
            pinned: post.pin_order !== null && post.pin_order !== undefined,
          })
        ),
      };
    }

    const newArrivalPeople = Array.isArray(people) ? orderNewArrivals(people) : [];
    if (newArrivalPeople.length) {
      next = {
        ...next,
        newArrivals: newArrivalPeople.slice(0, 8).map(
          (person): NewArrivalItem => ({
            id: person.user_id,
            name: person.name,
            detail: newArrivalDetail(person),
            ldap: person.ldap,
            photoUrl: person.profile_photo_url,
          })
        ),
      };
    }

    // "Meet someone" comes from people already cached for the logged-in user:
    // their direct reports, teammates, and reporting line.
    const featured = buildFeaturedPerson(
      directReports ?? [],
      teamMembers ?? [],
      reportingLine ?? [],
      employee?.user_id
    );
    if (featured) {
      next = {...next, featuredPerson: featured};
    }

    if (Array.isArray(gratitudes) && gratitudes.length) {
      const latest = gratitudes[0];
      next = {
        ...next,
        latestGratitude: {
          message: latest.message,
          senderName: latest.sender?.name ?? 'A colleague',
          recipientName: latest.recipient?.name ?? 'You',
        },
      };
    }

    if (employee?.time_zone) {
      next = {
        ...next,
        viewerZone: {timeZone: employee.time_zone, city: employee.city ?? undefined},
      };
    }

    next = {...next, ...loadingFlags};

    // reportingLine[0] is the viewer; [1] is their direct manager (+1 only).
    const manager = reportingLine?.[1];
    const circle = [
      ...(teamMembers ?? []),
      ...(manager ? [manager] : []),
      ...(directReports ?? []),
    ];
    // Until the employee map (the zone source) has loaded we clear timezone rows
    // and flag loading so the tile renders a skeleton; once loaded we show the
    // real groups (possibly empty, leaving just the viewer's own row).
    next = employeeMapFetched
      ? {
          ...next,
          timezoneGroups: buildTimezoneGroups(circle, employeeMap ?? [], employee?.user_id),
          timezonesLoading: false,
        }
      : {...next, timezoneGroups: [], timezonesLoading: true};

    // Overlay the viewer's real calendar. The agenda query degrades to [] on
    // error (no calendar linked, no consent). If a refetch times out server-side
    // and only company events come back, keep the last known personal rows.
    if (Array.isArray(agenda) && agenda.length) {
      const mapped = agenda.map(mapHomeAgendaItem);
      let resolved = mapped;
      if (
        !hasPersonalAgendaItems(mapped) &&
        hasPersonalAgendaItems(lastPersonalAgendaRef.current)
      ) {
        resolved = mergeAgendaWithStalePersonal(
          mapped,
          lastPersonalAgendaRef.current.filter((item) => item.source === 'personal')
        );
      }
      if (hasPersonalAgendaItems(resolved)) {
        lastPersonalAgendaRef.current = resolved.filter((item) => item.source === 'personal');
      }
      next = {...next, agenda: resolved};
    } else {
      next = {...next, agenda: []};
    }

    if (jiraFetched) {
      const jiraPayload = normalizeJiraResponse(jiraTickets);
      next = {
        ...next,
        jiraTickets: jiraPayload.tickets.map(
          (ticket): JiraTicket => ({
            id: ticket.id,
            key: ticket.key,
            title: ticket.title,
            counterpartyName: ticket.counterparty_name ?? undefined,
            isAssignedToViewer: Boolean(ticket.is_assigned_to_viewer),
            isReportedByViewer: Boolean(ticket.is_reported_by_viewer),
            status: toJiraStatus(ticket.status),
            url: ticket.url,
          })
        ),
        jiraIssuesUrl: jiraPayload.issues_url || undefined,
      };
    }

    return next;
  }, [
    state,
    stock,
    news,
    people,
    gratitudes,
    loadingFlags,
    employeeMap,
    employeeMapFetched,
    jiraTickets,
    jiraFetched,
    agenda,
    teamMembers,
    directReports,
    reportingLine,
    employee?.user_id,
    employee?.time_zone,
    employee?.city,
  ]);
}
